export {
  calculateAge,
  getAge,
  formatCalendarDate,
  formatCalendarDateForInput,
  getLocalCalendarDate,
} from "./calendarDate.mjs";
export { formatTimestampDate, formatTimestampDateForInput } from "./timestampDate.mjs";
/* eslint-disable unused-imports/no-unused-imports */
import { format, formatISO, parseISO, isValid } from "date-fns";

import { api_without_cancellation } from "../api/api";
import { setStatusLoader } from "../Redux/general/actions";
import { clearAccessToken, getAuthorizationToken, setAccessToken } from "../auth/tokenStore";

export const toTitleCase = (text = "") =>
  text.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

export const getToken = () => {
  return getAuthorizationToken();
};

export const setToken = (token) => {
  setAccessToken(token);
  return getAuthorizationToken();
};

export const removeToken = () => {
  clearAccessToken();
};

export const getClientId = () => {
  return localStorage.getItem("client_id");
};

export const setClientId = (client_id) => {
  return localStorage.setItem("client_id", client_id);
};

export const getCaseId = () => {
  return localStorage.getItem("case_id");
};

export const setCaseId = (case_id) => {
  localStorage.setItem("case_id", case_id);
  // Dispatch custom event to notify ChecklistWebSocketProvider of case change
  window.dispatchEvent(new CustomEvent("caseChanged", { detail: { caseId: case_id } }));
  return case_id;
};

export const currencyFormat = (amount) => {
  // Use Intl.NumberFormat for formatting
  let formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Math.abs(amount)); // Format without the negative sign initially

  // Add the negative sign after the dollar sign if the amount is negative
  if (amount < 0) {
    formatted = formatted.replace("$", "$ -");
  } else {
    formatted = formatted.replace("$", "$ ");
  }

  return formatted;
};

export const inputCurrencyFormat = (value) => {
  if (value === "" || isNaN(value)) return ""; // Keep empty when no valid input

  return (
    "$ " +
    new Intl.NumberFormat("en-US", {
      style: "decimal", // Keep it as 'decimal' to avoid automatic currency symbols
      minimumFractionDigits: 2, // Show decimals only when necessary
      maximumFractionDigits: 2, // Restrict to two decimal places
      useGrouping: true, // Enable comma separators (e.g., 1,234.56)
    }).format(value)
  );
};

export const inputCurrencyFormat2 = (value) => {
  if (value === "" || isNaN(value)) return ""; // Keep empty when no valid input

  return (
    "$ " +
    new Intl.NumberFormat("en-US", {
      style: "decimal", // Keep it as 'decimal' to avoid automatic currency symbols
      minimumFractionDigits: 0, // Show decimals only when necessary
      maximumFractionDigits: 4, // Restrict to two decimal places
      useGrouping: true, // Enable comma separators (e.g., 1,234.56)
    }).format(value)
  );
};

export function formatDate(dateStr) {
  if (!dateStr || dateStr === "" || dateStr === "__/__/___" || dateStr === "Invalid Date") {
    return "";
  }
  // Convert the input string to a Date object
  const date = new Date(dateStr);
  // Get the day, month, and year from the Date object
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString();
  const year = date.getFullYear();

  // Format the date in dd/mm/yyyy format
  return `${month}/${day}/${year}`;
}

export function formatToYYYYMMDD(dateStr) {
  const date = new Date(dateStr); // Parse the input date string
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2-digit month
  const day = String(date.getDate()).padStart(2, "0"); // Ensure 2-digit day
  return `${year}-${month}-${day}`;
}

export function formatDateUTC(dateStr) {
  if (!dateStr || dateStr === "" || dateStr === "__/__/___" || dateStr === "Invalid Date") {
    return "";
  }

  const date = new Date(dateStr);

  if (isNaN(date)) {
    return "";
  }

  const day = date.getUTCDate().toString();
  const month = (date.getUTCMonth() + 1).toString();
  const year = date.getUTCFullYear();

  return `${month}/${day}/${year}`;
}

export function formatDateTime(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 120) return "1 hour ago";

  // Otherwise show full datetime
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function getCurrentDate() {
  const today = new Date();
  return today.toISOString().split("T")[0]; // Formats as YYYY-MM-DD
}

export function getFutureDate(days) {
  const today = new Date();
  today.setDate(today.getDate() + days); // Add specified days
  return today.toISOString().split("T")[0]; // Format as YYYY-MM-DD
}

export function formatPhoneNumber(raw) {
  const digits = (raw ?? "").replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  // Remove non-digit characters from the phone number
  // phoneNumber = phoneNumber?.replace(/\D/g, "");

  // // Format the phone number as (XXX) XXX-XXXX
  // return phoneNumber?.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
}

export function formatPhoneToDashes(phoneNumber) {
  if (phoneNumber && phoneNumber !== "") {
    if (phoneNumber.length >= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    }
    return phoneNumber;
  } else {
    return "(###) ###-####";
  }
}

// media files route resolver
// U2024/31/5/3:19AM
export function mediaRoute(route) {
  const node_env = process.env.NODE_ENV;
  const media_origin = node_env === "production" ? "" : process.env.REACT_APP_BACKEND_URL;

  const absoluteUrl = media_origin + route;
  return absoluteUrl;
}

export const fetchPageObject = async (caseId, pageId) => {
  // Lazy-load store and action to avoid circular dependency
  const { default: store } = await import("../Redux/store");
  const { fetchAllPages_New } = await import("../Redux/pages/pagesSlice");

  const CACHE_DURATION = 60000; // 1 minute in milliseconds

  // Get current Redux state
  const state = store.getState();
  const allPages = state.allPages?.allPages;
  const lastFetched = state.allPages?.lastFetched;

  // Check if cache is stale
  const isCacheStale = !lastFetched || Date.now() - lastFetched > CACHE_DURATION;

  // If cache is fresh and page exists in Redux, return it
  if (!isCacheStale && allPages && Array.isArray(allPages)) {
    const pageFromRedux = allPages.find((page) => page.id === pageId);
    if (pageFromRedux) {
      return pageFromRedux;
    }
  }

  // If cache is stale or page not found, refresh all pages
  const origin = process.env.REACT_APP_BACKEND_URL;

  try {
    // Dispatch fetchAllPages_New to refresh Redux state
    await store.dispatch(fetchAllPages_New(`${origin}/api/litigation-page/get-pages/`));

    // After refreshing, try to get the page from Redux again
    const updatedState = store.getState();
    const updatedAllPages = updatedState.allPages?.allPages;

    if (updatedAllPages && Array.isArray(updatedAllPages)) {
      const pageFromUpdatedRedux = updatedAllPages.find((page) => page.id === pageId);
      if (pageFromUpdatedRedux) {
        return pageFromUpdatedRedux;
      }
    }

    // If still not found after refresh, fallback to individual page API call
    const baseUrl = process.env.REACT_APP_BACKEND_URL + "/api/get-page/";
    const url = `${baseUrl}?case_id=${caseId}&page_id=${pageId}`;
    const response = await api_without_cancellation.get(url);
    return response?.data;
  } catch (error) {
    console.warn("Error fetching page:", error);
    throw error;
  }
};

//U2024/12/6|10:58PM
export const fetchShakespeareStatus = async (caseId, pageId, dispatch) => {
  dispatch(setStatusLoader(true));

  try {
    // const page = await fetchPageObject(caseId, entity, pageId);

    // if (page?.id) {
    const baseUrl = process.env.REACT_APP_BACKEND_URL + "/api/shakespeare/status/";

    const url = `${baseUrl}?page_id=${pageId}&case_id=${caseId}`;

    const response = await api_without_cancellation.get(url);
    return response.data;
    // } else {
    //   return null;
    // }
  } catch (error) {
    console.error("Error fetching Shakespeare status:", error);
  } finally {
    dispatch(setStatusLoader(false));
  }
};

// FUNCTION FOR FORMATING DATE TO STANDARD FORMAT (MM/DD/YYYY)
export function standardFormatDate(date) {
  return date
    ? new Date(date)
        .toLocaleDateString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        })
        .replace(/^0+/, "")
    : "";
}

export function formatDateAtTime(dateStr) {
  if (!dateStr || dateStr === "" || dateStr === "__/__/___" || dateStr === "Invalid Date") {
    return "";
  }

  const date = new Date(dateStr);

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2);

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const ampm = hours >= 12 ? " pm" : " am";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
}

export function formatDateAtTimeTodosCompletion(dateStr) {
  if (!dateStr || dateStr === "" || dateStr === "__/__/___" || dateStr === "Invalid Date") {
    return "";
  }

  const date = new Date(dateStr);

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2);

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  const ampm = hours >= 12 ? " pm" : " am";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${month}/${day}/${year} at ${hours}:${minutes}:${seconds}${ampm}`;
}

export function formatDateAtTime2(dateStr) {
  if (!dateStr || dateStr === "" || dateStr === "__/__/___" || dateStr === "Invalid Date") {
    return "";
  }

  const date = new Date(dateStr);

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString();

  // Get hours and minutes
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const ampm = hours >= 12 ? " pm" : " am";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
}

// FUNCTION FOR FORMATING DATE TIME TO STANDARD FORMAT (MM/DD/YYYY)
export function standardFormatDateTime(isoDate) {
  const date = new Date(isoDate);
  // Format the date and time
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };
  const formattedDate = date.toLocaleDateString("en-US", options).replace(",", " at");
  return formattedDate;
}

//US:2024/AUG/6
export function isValidUSPhoneNumber(phoneNumber) {
  const usPhonePattern = /^(\+1|1)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  return usPhonePattern.test(phoneNumber);
}

// Set width of a table columns Equal.
export const setEqualColumnWidths = (tableRef) => {
  const table = tableRef.current;

  if (table) {
    const tableRect = table.getBoundingClientRect();
    const availableWidth = window.innerWidth - tableRect.left;

    const headers = table.querySelectorAll("th");
    const numColumns = headers.length;

    if (numColumns > 0 && availableWidth > 0) {
      const columnWidth = Math.floor(availableWidth / numColumns);

      headers.forEach((header) => {
        header.style.minWidth = `${columnWidth}px`;
        header.style.maxWidth = `${columnWidth}px`;
      });

      const cells = table.querySelectorAll("td");
      cells.forEach((cell) => {
        cell.style.minWidth = `${columnWidth}px`;
        cell.style.maxWidth = `${columnWidth}px`;
      });
    }
  }
};

export const setEqualColumnWidthsState = (tableRef) => {
  const table = tableRef.current;

  if (table) {
    const availableWidth = window.innerWidth - 322;

    const headers = table.querySelectorAll("th");
    const numColumns = headers.length;

    if (numColumns > 0 && availableWidth > 0) {
      const columnWidth = Math.floor(availableWidth / numColumns);

      headers.forEach((header) => {
        header.style.minWidth = `${columnWidth}px`;
        header.style.maxWidth = `${columnWidth}px`;
      });

      const cells = table.querySelectorAll("td");
      cells.forEach((cell) => {
        cell.style.minWidth = `${columnWidth}px`;
        cell.style.maxWidth = `${columnWidth}px`;
      });
    }
  }
};

export const setEqualColumnWidthsCounty = (tableRef) => {
  const table = tableRef.current;

  if (table) {
    const availableWidth = window.innerWidth - 322;

    const headers = table.querySelectorAll("th");
    const numColumns = headers.length;

    if (numColumns > 0 && availableWidth > 0) {
      const columnWidth = Math.floor(availableWidth / numColumns);

      headers.forEach((header) => {
        header.style.minWidth = `${columnWidth}px`;
        header.style.maxWidth = `${columnWidth}px`;
      });

      const cells = table.querySelectorAll("td");
      cells.forEach((cell) => {
        cell.style.minWidth = `${columnWidth}px`;
        cell.style.maxWidth = `${columnWidth}px`;
      });
    }
  }
};

export const setEqualColumnWidthsInjury = (tableRef) => {
  const table = tableRef.current;

  if (table) {
    const availableWidth = window.innerWidth - 322;

    const headers = table.querySelectorAll("th");
    const numColumns = headers.length;

    if (numColumns > 0 && availableWidth > 0) {
      const columnWidth = Math.floor(availableWidth / numColumns);

      headers.forEach((header) => {
        header.style.minWidth = `${columnWidth}px`;
        header.style.maxWidth = `${columnWidth}px`;
      });

      const cells = table.querySelectorAll("td");
      cells.forEach((cell) => {
        cell.style.minWidth = `${columnWidth}px`;
        cell.style.maxWidth = `${columnWidth}px`;
      });
    }
  }
};

export const getLoggedInUserId = () => {
  const userId = localStorage.getItem("loggedInUser") || null;
  return userId;
};

export const getLoggedInFirmId = () => {
  const firmId = localStorage.getItem("firm_id") || null;
  return firmId;
};

export const getLoggedInUserPf = () => {
  const userPf = localStorage.getItem("loggedInUserPf") || null;
  return userPf;
};

export const getLoggedInUserName = () => {
  const fullName = localStorage.getItem("loggedInUserName") || null;
  return fullName;
};

//DEV_RK 9/5/2024
// :: These formatting functions address the issue of day shift bugs for ISO dates. ::

// Formats date to ISO 8601 format (complete representation) for posting data
export const formatDateForSubmission = (date) => {
  const parsedDate = parseISO(date);
  return formatISO(parsedDate, { representation: "complete" }); // Ensures date is in complete ISO 8601 format in UTC
};

// Formats date to 'yyyy-MM-dd' format for populating fields in modals
export const formatDateForModalFields = (date) => {
  if (!date) return "";
  const parsedDate = parseISO(date);
  return format(parsedDate, "yyyy-MM-dd"); // Formats date for display in input fields within modals
};

// Formats date to 'M/dd/yyyy' format for rendering in UI panels
export const formatDateForPanelDisplay = (value) => {
  if (!value) return "";

  let parsedDate;

  if (value instanceof Date) {
    parsedDate = value;
  } else if (typeof value === "string") {
    parsedDate = parseISO(value);
  } else if (typeof value === "number") {
    parsedDate = new Date(value);
  } else {
    return "";
  }

  if (!isValid(parsedDate)) return "";

  return format(parsedDate, "M/dd/yyyy");
};

export const formatPhoneNumberAndRemoveDashes = (phoneNumber) => {
  if (phoneNumber) {
    const cleaned = phoneNumber?.replace(/\D/g, ""); // Remove all non-digit characters
    const match = cleaned?.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return "(###) ###-####"; // Return null if the input is not a valid phone number
  }
  return "(###) ###-####";
};

export const formatChequeData = (chequeData) => {
  if (!chequeData) return null;

  const parts = chequeData.split("-");
  if (parts.length !== 3) return null;

  const [year, month, day] = parts;
  return `${month}/${day.padStart(2, "0")}/${year}`;
};

export const insuranceStyles = (hasValue) => ({
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    height: "38px",
    borderRadius: "4px",
    borderColor: hasValue ? "#28a745" : state.isFocused ? "#ced4da" : "#ced4da",
    boxShadow: state.isFocused ? "none" : "none",
    "&:hover": {
      borderColor: hasValue ? "#28a745" : "#ced4da",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: "38px",
    padding: "0 10px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "38px",
    display: "flex",
    alignItems: "center", // ✅ keeps arrow centered vertically
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px", // smaller padding so it doesn’t push down
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
});

export const getFieldClass = (value, required = false) => {
  if (!value || value === 0 || value === "0" || value === "") {
    return required ? "form-control haddress2" : "form-control";
  }
  return "form-control has-value";
};

export function inputPhoneNumber(phoneNumber) {
  phoneNumber = phoneNumber.replace(/\D/g, "");

  if (phoneNumber.length > 10) {
    phoneNumber = phoneNumber.slice(0, 10);
  }

  if (phoneNumber.length === 0) {
    return "";
  } else if (phoneNumber.length < 4) {
    return `(${phoneNumber}`;
  } else if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
}

export const reformatDate = (date) => {
  const dateFormats = [/^\d{4}-\d{2}-\d{2}$/, /^\d{4}\/\d{2}\/\d{2}$/, /^\d{2}\/\d{2}\/\d{4}$/];

  let formattedDate = null;

  dateFormats.forEach((pattern) => {
    if (pattern.test(date)) {
      const parts = date.split(/[-/]/);
      if (parts.length === 3) {
        const [year, month, day] = parts;
        if (parts[0].length === 4) {
          formattedDate = `${month}/${day}/${year}`;
        } else {
          formattedDate = date;
        }
      }
    }
  });

  return formattedDate;
};

export const formatWithCommas = (value) => {
  if (!value || isNaN(Number(value))) {
    return "";
  }

  const formattedValue = Number(value).toLocaleString();
  return formattedValue;
};

export const addClickRecord = async (clickId, pageId) => {
  try {
    const payload = {
      page_id: pageId,
      click: clickId,
      case_id: getCaseId(),
      client_id: getClientId(),
    };
    await api_without_cancellation.post("/api/general/click_record/", payload);
  } catch (error) {
    console.error(`Failed to add click record for ${clickId}:`, error);
  }
};
